import { getDomain, parse as parseHost } from 'tldts';
/**
 * Canonical identities used as database partition keys.
 *
 * Plan section 17 item 1: these can never change without a data migration, so
 * they live in one place, are pure, and are exhaustively tested.
 */
const LINKEDIN_HOST = /(^|\.)linkedin\.com$/i;
/** Registrable domain: `www.Foo.co.uk/path` -> `foo.co.uk`. Returns null when there isn't one. */
export function registrableDomain(input) {
    if (!input)
        return null;
    let s = String(input).trim().toLowerCase();
    if (!s)
        return null;
    // Accept bare domains, full URLs, and things with stray whitespace or mailto:.
    s = s.replace(/^mailto:/, '');
    // An address's domain follows its @ — but a URL's @ is in its path (a YouTube
    // handle, a Medium author), and the part after it is not a domain at all.
    const isUrl = /^[a-z][a-z0-9+.-]*:\/\//.test(s);
    if (!isUrl && s.includes('@'))
        s = s.slice(s.lastIndexOf('@') + 1);
    if (!isUrl)
        s = `https://${s}`;
    let host;
    try {
        host = new URL(s).hostname;
    }
    catch {
        return null;
    }
    /*
     * Suffixes the bundled Public Suffix List does not know yet. India's banks
     * moved to `<bank>.bank.in` and regulated finance companies to `.fin.in`;
     * read as ordinary domains, every bank in the country is one company called
     * `bank.in`, and an address at one bank grades as company-domain at another.
     */
    for (const suffix of LATE_SUFFIXES) {
        if (host === suffix)
            return null;
        if (host.endsWith(`.${suffix}`)) {
            const label = host
                .slice(0, -(suffix.length + 1))
                .split('.')
                .pop();
            return label ? `${label}.${suffix}` : null;
        }
    }
    const d = getDomain(host);
    return d ? d.toLowerCase() : null;
}
const LATE_SUFFIXES = ['bank.in', 'fin.in'];
/**
 * Every registrable domain in one spreadsheet cell.
 *
 * A website column is filled in by people, and people put more than one company
 * domain in it: "eynetech.com,deskday.com,deskday.ai" is a real row from a real
 * upload. Read as a single host that is nonsense, so the row was warned about
 * and searched by name only — throwing away three perfectly good domains, one of
 * which was the company's actual site.
 *
 * Split on the separators a person actually types. Order is preserved and
 * duplicates removed, because the FIRST domain is treated as the company's own
 * and the rest become aliases: an address at any of them is still that company's
 * address, which is what the email grade needs to know.
 */
export function registrableDomains(input) {
    if (!input)
        return [];
    const out = [];
    for (const part of String(input).split(/[,;|\s]+/)) {
        const domain = registrableDomain(part);
        if (domain && !out.includes(domain))
            out.push(domain);
    }
    return out;
}
/** The domain part of an email address, as a registrable domain. */
export function emailDomain(email) {
    if (!email?.includes('@'))
        return null;
    return registrableDomain(email.slice(email.lastIndexOf('@') + 1));
}
/** True when two domains are the same registrable domain. Null never equals anything. */
export function sameDomain(a, b) {
    const x = registrableDomain(a);
    const y = registrableDomain(b);
    return x !== null && y !== null && x === y;
}
function linkedinPath(raw) {
    let s = String(raw).trim();
    if (!s)
        return null;
    if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s))
        s = `https://${s}`;
    let u;
    try {
        u = new URL(s);
    }
    catch {
        return null;
    }
    const host = u.hostname.toLowerCase();
    if (!LINKEDIN_HOST.test(host))
        return null;
    const segments = u.pathname
        .split('/')
        .filter(Boolean)
        .map((x) => decodeURIComponent(x));
    return { host, segments };
}
/**
 * Canonical LinkedIn *company* URL: `https://www.linkedin.com/company/<slug>`.
 *
 * Folds country subdomains (`in.linkedin.com`), strips query strings, trailing
 * slashes and sub-paths such as `/about`, and lowercases the slug. Returns null
 * for anything that is not a company URL, including school URLs, which are a
 * different entity type that must never compare equal to a company.
 */
export function canonCompanyUrl(raw) {
    if (!raw)
        return null;
    const p = linkedinPath(raw);
    if (!p)
        return null;
    const [kind, slug] = p.segments;
    if (!slug)
        return null;
    if (kind !== 'company' && kind !== 'showcase' && kind !== 'organization')
        return null;
    const clean = slug.toLowerCase().trim();
    if (!clean)
        return null;
    return `https://www.linkedin.com/company/${clean}`;
}
/**
 * Canonical LinkedIn *person* URL: `https://www.linkedin.com/in/<slug>`.
 *
 * This is the Contacts partition key. Country subdomains and trailing paths
 * fold, so one human is one row and is never enriched twice.
 */
export function canonProfileUrl(raw) {
    if (!raw)
        return null;
    const p = linkedinPath(raw);
    if (!p)
        return null;
    const [kind, slug] = p.segments;
    if (kind !== 'in' || !slug)
        return null;
    const clean = slug.toLowerCase().trim();
    if (!clean)
        return null;
    return `https://www.linkedin.com/in/${clean}`;
}
/**
 * A person the CRM holds and LinkedIn does not — or not that anyone has recorded.
 *
 * The contacts table is keyed by canonical profile URL because that is the one
 * identity a provider can be asked about. Zoho CRM holds hundreds of people with
 * an email, a mobile and no LinkedIn URL at all, and they have to live in the
 * same table: they are exactly the people a later job must not buy again. So a
 * CRM record with no profile URL is keyed by the CRM's own id, in a shape no
 * LinkedIn URL can take, and the two kinds of key never collide. The module is
 * part of the key so a Lead and a Contact with the same Zoho id are two rows.
 */
export const CRM_CONTACT_KEY = /^crm:zoho:(contact|lead):(\d{5,25})$/;
export function crmContactKey(module, zohoId) {
    return `crm:zoho:${module}:${zohoId}`;
}
export function parseCrmContactKey(key) {
    const m = key ? CRM_CONTACT_KEY.exec(key) : null;
    return m ? { module: m[1], zohoId: m[2] } : null;
}
/** A key that names a LinkedIn profile, as opposed to a CRM record. */
export function isProfileKey(key) {
    return typeof key === 'string' && key.startsWith('https://www.linkedin.com/in/');
}
export function isCrmKey(key) {
    return typeof key === 'string' && CRM_CONTACT_KEY.test(key);
}
/**
 * The ONE canonicaliser for a contacts-table key: a LinkedIn profile URL in any
 * spelling, or a CRM key verbatim. Everything that reads a contact by key goes
 * through here, so a CRM-only person is reachable everywhere a LinkedIn one is.
 */
export function canonContactKey(raw) {
    if (!raw)
        return null;
    const url = canonProfileUrl(raw);
    if (url)
        return url;
    const trimmed = raw.trim();
    return CRM_CONTACT_KEY.test(trimmed) ? trimmed : null;
}
/** True when two company URLs denote the same company. The ONLY legal comparison. */
export function sameCompanyUrl(a, b) {
    const x = canonCompanyUrl(a);
    const y = canonCompanyUrl(b);
    return x !== null && y !== null && x === y;
}
/** True when two profile URLs denote the same person. */
export function sameProfileUrl(a, b) {
    const x = canonProfileUrl(a);
    const y = canonProfileUrl(b);
    return x !== null && y !== null && x === y;
}
/** Is this host a public suffix only (e.g. someone pasted "co.uk")? */
export function isBareSuffix(input) {
    const p = parseHost(input);
    return Boolean(p.publicSuffix && !p.domainWithoutSuffix);
}
//# sourceMappingURL=keys.js.map